/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { t } from '@sportninja/common/i18n'
import { ROLE_TYPE_ID_TO_STRING } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { Table, Spin } from 'antd'
import { EditOutlined } from '@ant-design/icons'

import { SendInvitationTrigger } from '../SendInvitationModal'
import OptionButtonDelete from './OptionButtons/Delete'
import PersonCell from './PersonCell'
import MoreOptions from './OptionButtons/More'
import useUserList from './useUserList'
import useWindowSize from 'src/hooks/useWindowSize'
import { getTableStyles } from 'src/styles/tableStyles'
import { formatPhoneNumber } from 'react-phone-number-input'
import UserModal from '../UserModal'

const SCROLL_THRESHOLD = 500

const UserListTable = ({ entityType, entityId, slug, refreshKey }) => {
  const [isUserModalOpen, setIsUserModalOpen] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 1024, [width])

  const { items, loading, hasMore, loadMore, refresh } = useUserList(slug)

  // Ref to prevent multiple simultaneous loadMore calls
  const isLoadingMoreRef = useRef(false)
  // Ref to track initial mount and prevent refresh on first render
  const isInitialMountRef = useRef(true)

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      return
    }

    if (refreshKey !== undefined) {
      refresh()
    }
  }, [refreshKey, refresh])

  const handleScroll = React.useCallback(() => {
    if (loading || isLoadingMoreRef.current) {
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = document.documentElement
    const nearBottom =
      scrollHeight - (scrollTop + clientHeight) < SCROLL_THRESHOLD
    if (nearBottom && hasMore) {
      isLoadingMoreRef.current = true
      loadMore()
    }
  }, [hasMore, loading, loadMore])

  useEffect(() => {
    if (!loading) {
      isLoadingMoreRef.current = false
    }
  }, [loading])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  const columns = useMemo(() => {
    const cols = [
      {
        title: t('common:name'),
        key: 'name_last',
        dataIndex: 'name_last',
        width: 300,
        sorter: (a, b) => {
          const nameA = `${a.name_last || ''} ${a.name_first || ''}`
          const nameB = `${b.name_last || ''} ${b.name_first || ''}`
          return nameA.localeCompare(nameB)
        },
        render: (_, item) => <PersonCell item={item} />,
      },
      {
        title: 'Role',
        key: 'role_type_id',
        dataIndex: 'role_type_id',
        sorter: (a, b) => (a.role_type_id || 0) - (b.role_type_id || 0),
        render: (roleId) => ROLE_TYPE_ID_TO_STRING[roleId] || '',
      },
      {
        title: t('common:email'),
        key: 'email',
        dataIndex: 'email',
        sorter: (a, b) => (a.email || '').localeCompare(b.email || ''),
        render: (email) => email || '',
      },
      {
        title: t('common:phone'),
        key: 'phone_number',
        dataIndex: 'phone_number',
        sorter: (a, b) =>
          (a.phone_number || '').localeCompare(b.phone_number || ''),
        render: (phoneNumber) => formatPhoneNumber(phoneNumber) || '',
      },
      {
        title: 'Invite',
        key: 'invite_status',
        sorter: (a, b) => {
          const order = { accepted: 2, invited: 1, none: 0 }
          const statusA = a.is_accepted
            ? 'accepted'
            : a.is_invited
            ? 'invited'
            : 'none'
          const statusB = b.is_accepted
            ? 'accepted'
            : b.is_invited
            ? 'invited'
            : 'none'
          return order[statusA] - order[statusB]
        },
        render: (_, item) => {
          if (item?.is_accepted) {
            return 'Accepted'
          }
          if (item?.is_invited) {
            return 'Invited'
          }
          return '--'
        },
      },
      {
        // title: 'Options',
        title: '',
        key: 'actions',
        align: 'right',
        width: 150,
        fixed: 'right',
        render: (_, item) => {
          if (item.role_type_id === 0) {
            return null
          }

          let itemSlug = `/${entityType}/${entityId}/permissions/${item.id}`

          if (!item?.is_accepted) {
            itemSlug = `/invitations/${item.invitation_id}`
          }

          const handleEditClick = () => {
            setEditingUser(item)
            setIsUserModalOpen(true)
          }

          return (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                justify-content: flex-end;
              `}
            >
              <button
                type='button'
                onClick={handleEditClick}
                css={css`
                  background: transparent;
                  border: none;
                  cursor: pointer;
                  color: ${colors.PRIMARY};
                  padding: 4px 8px;
                  font-size: 18px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  &:hover {
                    opacity: 0.8;
                  }
                `}
                title={t('Web:editAdmin')}
              >
                <EditOutlined
                  css={css`
                    font-size: 20px;
                  `}
                />
              </button>
              <SendInvitationTrigger
                invite={{
                  is_invited: item.is_invited,
                  is_accepted: item.is_accepted,
                  invitation_id: item.invitation_id,
                }}
                user={{
                  id: item.id,
                  name_first: item.name_first,
                  name_last: item.name_last,
                  role_type_id: item.role_type,
                  email: item?.email,
                }}
                slug={itemSlug}
                onComplete={refresh}
              />
              <MoreOptions
                options={[
                  {
                    key: 'remove',
                    Component: OptionButtonDelete,
                    componentProps: {
                      buttonText: 'remove',
                      slug: itemSlug,
                      itemId:
                        item?.is_accepted && item?.is_accepted === true
                          ? item.id
                          : item.invitation_id,
                      iconName: 'user-minus',
                      title: `Remove ${item.name_first} ${item.name_last}`,
                      type: t('common:user'),
                      onComplete: refresh,
                    },
                  },
                ]}
              />
            </div>
          )
        },
      },
    ]

    return cols
  }, [entityType, entityId, refresh])

  // Deduplicate items as a safety net (API may return overlapping pages)
  const uniqueItems = useMemo(() => {
    const seen = new Set()
    return items.filter((item) => {
      const key = item.id || item.invitation_id
      if (seen.has(key)) {
        return false
      }
      seen.add(key)
      return true
    })
  }, [items])

  return (
    <div
      css={css`
        width: 100%;
        margin-top: 24px;
        margin-bottom: 100px;
      `}
    >
      {loading && items.length === 0 ? (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 200px;
          `}
        >
          <Spin
            size='small'
            css={css`
              .ant-spin-dot-item {
                background-color: ${colors.PRIMARY};
              }
            `}
          />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={uniqueItems}
          rowKey={(item) => item.id || item.invitation_id}
          pagination={false}
          size='small'
          scroll={{ x: 'max-content' }}
          css={getTableStyles(isMobile)}
        />
      )}
      {loading && items.length > 0 && (
        <div
          css={css`
            display: flex;
            justify-content: center;
            padding: 20px;
          `}
        >
          <Spin size='small' color={colors.PRIMARY} />
        </div>
      )}
      <UserModal
        isOpen={isUserModalOpen}
        onClose={() => {
          setIsUserModalOpen(false)
          setEditingUser(null)
        }}
        initialData={editingUser}
        entityType={entityType}
        entityId={entityId}
        onSuccess={() => {
          refresh()
          setIsUserModalOpen(false)
          setEditingUser(null)
        }}
      />
    </div>
  )
}

export default UserListTable
